// SimpleTradesHistory/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
  TextField,
  Button,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TableChart as TableChartIcon,
  FilterAlt as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { COLORS, formatPercent } from './constants';
import SimpleTradeTable from './SimpleTradeTable';

const SimpleTradesHistory = ({ apiBaseUrl = window.location.origin }) => {
  // State
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [asset, setAsset] = useState('NQ');
  const [direction, setDirection] = useState('LONG');
  const [timeframe, setTimeframe] = useState('1m');
  const [tradingHour, setTradingHour] = useState('ETH');
  const [startDate, setStartDate] = useState('2025-07-01'); // Start from July 2025
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  // Fetch trades
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        asset,
        direction,
        timeframe,
        signal_type: timeframe === '45s' ? 'default' : 'standard',
        trading_hour: tradingHour,
        start_date: startDate,
        end_date: endDate,
      });

      const url = `${apiBaseUrl}/strategies/api/simple-trades/?${params}`;
      console.log('[SimpleTradesHistory] Fetching:', url);

      const response = await axios.get(url);
      console.log('[SimpleTradesHistory] Response:', response.data);

      if (response.data.success) {
        setData(response.data);
      } else {
        setError(response.data.error || 'Failed to load trades');
      }
    } catch (err) {
      console.error('[SimpleTradesHistory] Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, asset, direction, timeframe, tradingHour, startDate, endDate]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export CSV - dynamic columns
  const handleExportCSV = () => {
    if (!data?.trades?.length) return;

    // Get dynamic tracked signal columns from first trade
    const standardFields = ['Trade', 'Date', 'Entry Time', 'Exit Time', 'Entry Price', 'Exit Price', 'PnL', 'PnL_Pct'];
    const trackedSignals = Object.keys(data.trades[0]).filter(key => !standardFields.includes(key));

    // Build headers dynamically
    const headers = [
      'Trade #', 'Date',
      ...trackedSignals,
      'Entry Time', 'Exit Time',
      'Entry Price', 'Exit Price',
      'PnL (pts)', 'PnL %'
    ];

    const rows = data.trades.map((t, idx) => [
      idx + 1,
      t.Date,
      ...trackedSignals.map(sig => t[sig] || ''),
      t['Entry Time'],
      t['Exit Time'],
      t['Entry Price'],
      t['Exit Price'],
      t.PnL,
      t.PnL_Pct
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `simple_trades_${asset}_${direction}_${timeframe}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Summary stats
  const summary = data?.summary || {};
  const trades = data?.trades || [];
  const totalPnl = summary.total_pnl || 0;
  const totalPnlPct = summary.total_pnl_pct || 0;
  const winRate = summary.win_rate || 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.bg.primary, p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: alpha(COLORS.bg.secondary, 0.8),
          border: `1px solid ${alpha(COLORS.border, 0.5)}`,
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.purple} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TableChartIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>
                  Simple Trades
                </Typography>
                <Chip
                  icon={direction === 'LONG' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                  label={direction}
                  size="small"
                  sx={{
                    bgcolor: alpha(direction === 'LONG' ? COLORS.success : COLORS.danger, 0.15),
                    color: direction === 'LONG' ? COLORS.success : COLORS.danger,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: direction === 'LONG' ? COLORS.success : COLORS.danger },
                  }}
                />
                {data && (
                  <Chip
                    label={`${trades.length} Trades`}
                    size="small"
                    sx={{
                      bgcolor: alpha(COLORS.primary, 0.15),
                      color: COLORS.primary,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Stack>
              {data && (
                <Typography variant="body2" sx={{ color: COLORS.text.muted, mt: 0.5 }}>
                  Win Rate: {winRate.toFixed(1)}% • Total P&L: {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)} pts • Return: {formatPercent(totalPnlPct)}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              onClick={handleExportCSV}
              disabled={!trades.length}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.muted,
                '&:hover': { bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary },
                '&.Mui-disabled': { color: alpha(COLORS.text.muted, 0.3) },
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.primary,
                '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: alpha(COLORS.bg.secondary, 0.8),
          border: `1px solid ${alpha(COLORS.border, 0.5)}`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterIcon sx={{ color: COLORS.primary, fontSize: 18 }} />
          <Typography sx={{ color: COLORS.text.primary, fontWeight: 600, fontSize: '0.9rem' }}>
            Filters
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-end">
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>Asset</Typography>
            <Select
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.primary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& .MuiSelect-icon': { color: COLORS.text.muted },
              }}
            >
              <MenuItem value="NQ">NQ</MenuItem>
              <MenuItem value="ES">ES</MenuItem>
              <MenuItem value="YM">YM</MenuItem>
              <MenuItem value="ETH">ETH</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>Direction</Typography>
            <Select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.primary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& .MuiSelect-icon': { color: COLORS.text.muted },
              }}
            >
              <MenuItem value="LONG">LONG</MenuItem>
              <MenuItem value="SHORT">SHORT</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>Timeframe</Typography>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.primary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& .MuiSelect-icon': { color: COLORS.text.muted },
              }}
            >
              <MenuItem value="45s">45s</MenuItem>
              <MenuItem value="1m">1m</MenuItem>
              <MenuItem value="5m">5m</MenuItem>
              <MenuItem value="15m">15m</MenuItem>
              <MenuItem value="1h">1h</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>Session</Typography>
            <Select
              value={tradingHour}
              onChange={(e) => setTradingHour(e.target.value)}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.primary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& .MuiSelect-icon': { color: COLORS.text.muted },
              }}
            >
              <MenuItem value="RTH">RTH</MenuItem>
              <MenuItem value="ETH">ETH</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>Start Date</Typography>
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{
                minWidth: 140,
                '& .MuiInputBase-root': {
                  bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                  color: COLORS.text.primary,
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& input': { color: COLORS.text.primary },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.65rem', mb: 0.5 }}>End Date</Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{
                minWidth: 140,
                '& .MuiInputBase-root': {
                  bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                  color: COLORS.text.primary,
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.border, 0.5) },
                '& input': { color: COLORS.text.primary },
              }}
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={fetchData}
            disabled={loading}
            sx={{
              bgcolor: COLORS.primary,
              color: '#000',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              '&:hover': { bgcolor: alpha(COLORS.primary, 0.8) },
            }}
          >
            {loading ? 'Loading...' : 'Search'}
          </Button>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3, bgcolor: alpha(COLORS.danger, 0.1) }}
        >
          {error}
        </Alert>
      )}

      {/* Loading state - initial */}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: COLORS.primary }} />
        </Box>
      )}

      {/* Loading overlay - refetch */}
      {loading && data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ color: COLORS.primary }} />
        </Box>
      )}

      {/* Trade Table */}
      {!loading && data && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: alpha(COLORS.bg.secondary, 0.8),
            border: `1px solid ${alpha(COLORS.border, 0.5)}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TableChartIcon sx={{ color: COLORS.primary }} />
              <Typography variant="h6" sx={{ color: COLORS.text.primary, fontWeight: 600 }}>
                Trade Log
              </Typography>
              <Chip
                label={`${trades.length} trades`}
                size="small"
                sx={{
                  bgcolor: alpha(COLORS.primary, 0.15),
                  color: COLORS.primary,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
              <Chip
                label={`${asset} • ${timeframe} • ${tradingHour}`}
                size="small"
                sx={{
                  bgcolor: alpha(COLORS.warning, 0.15),
                  color: COLORS.warning,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                }}
              />
            </Stack>
          </Box>

          <SimpleTradeTable trades={trades} direction={direction} />
        </Paper>
      )}

      {/* Empty state after search */}
      {!loading && data && trades.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            bgcolor: alpha(COLORS.bg.secondary, 0.8),
            border: `1px solid ${alpha(COLORS.border, 0.5)}`,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <TableChartIcon sx={{ fontSize: 64, color: alpha(COLORS.text.muted, 0.3), mb: 2 }} />
          <Typography variant="h6" sx={{ color: COLORS.text.muted, mb: 0.5 }}>
            No trades found
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.6) }}>
            Try adjusting the filters or date range
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SimpleTradesHistory;
